import { Button, TextField } from '@finpay-development/shared-components';
import { Grid, Typography, Button as MUIButton, Box } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { AttachMoney } from '@mui/icons-material';
import moment from 'moment';
import { formatNumberToUSD } from 'src/shared/calculators';
import type { PaymentProgramViewContext } from './use-payment-program-view';

const AttachMoneyIcon = AttachMoney;

interface CustomResultsViewProps {
  ctx: PaymentProgramViewContext;
}

/**
 * Custom-results step: input fields, results table, Back, Process HRE (if high risk), Add Payment Details.
 */
export function CustomResultsView({ ctx }: CustomResultsViewProps) {
  const {
    isMobile,
    billingFrequency,
    showDownPayment,
    showRecurringAmount,
    customDownPayment,
    customRecurringAmount,
    downPaymentError,
    recurringAmountError,
    optDownPayment,
    recurringAmt,
    customDownPmt,
    calculatedTerm,
    calculatedTermDisplay,
    customRecurringAmtFinal,
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    isHighRisk,
    patientId,
    encounterId,
    navigate,
    handleBack,
    handleSelectCustomProgram,
    handleProcessHre,
    handleDownPaymentChange,
    handleRecurringAmountChange,
    handleDownPaymentBlur,
    handleRecurringAmountBlur,
    classes,
  } = ctx;

  const isWeekly = billingFrequency === 'weekly';
  const termLabel = isWeekly ? (calculatedTermDisplay > 1 ? 'weeks' : 'week') : (calculatedTermDisplay > 1 ? 'months' : 'month');
  const eachPeriod = isWeekly ? 'each week' : 'each month';

  return (
    <Grid item xs={12}>
      {/* Input fields */}
      {(showDownPayment || showRecurringAmount) && (
        <Grid container className="mt-4" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" textAlign="left" sx={{ mb: 2, fontSize: '2rem', lineHeight: '2.6rem' }}>
              How close can you get to the {formatNumberToUSD(optDownPayment)} down payment and still feel comfortable?
            </Typography>
          </Grid>
          {showDownPayment && (
            <Grid item xs={12} key="down-payment-field" zeroMinWidth sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '250px minmax(0, 1fr)',
                  gap: 2,
                  width: '100%',
                  minWidth: 0,
                  alignItems: 'center',
                }}
              >
                <Box sx={{ minWidth: 0, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                  <TextField
                    label="Affordable Down Payment"
                    name="downPayment"
                    type="text"
                    value={customDownPayment}
                    onChange={handleDownPaymentChange}
                    onBlur={handleDownPaymentBlur}
                    error={!!downPaymentError}
                    helperText={downPaymentError}
                    placeholder="0.00"
                    startAdornment={<AttachMoneyIcon />}
                    test-id="custom-down-payment"
                  />
                </Box>
                {minDownPaymentPreapproved != null && (
                  <Box sx={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ color: 'error.main', width: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}
                    >
                      The minimum pre-approved down payment is {formatNumberToUSD(minDownPaymentPreapproved)}. Can we
                      bridge that gap? (Prompt Patient Champion)
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
          {showDownPayment && showRecurringAmount && (
            <Grid item xs={12}>
              <Typography variant="body2" textAlign="left" sx={{ fontSize: '2rem', lineHeight: '2.6rem' }}>
                How close can you get to the {formatNumberToUSD(recurringAmt)} {isWeekly ? 'weekly' : 'monthly'}{' '}
                recurring payment and still feel comfortable?
              </Typography>
            </Grid>
          )}
          {showRecurringAmount && (
            <Grid item xs={12} key="recurring-amount-field" zeroMinWidth sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '250px minmax(0, 1fr)',
                  gap: 2,
                  width: '100%',
                  minWidth: 0,
                  alignItems: 'center',
                }}
              >
                <Box sx={{ minWidth: 0, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                  <TextField
                    label="Affordable Recurring Payment"
                    name="recurringAmount"
                    type="text"
                    value={customRecurringAmount}
                    onChange={handleRecurringAmountChange}
                    onBlur={handleRecurringAmountBlur}
                    error={!!recurringAmountError}
                    helperText={recurringAmountError}
                    placeholder="0.00"
                    startAdornment={<AttachMoneyIcon />}
                    test-id="custom-recurring-amount"
                  />
                </Box>
                {minRecurringPreapproved != null && (
                  <Box sx={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ color: 'error.main', width: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}
                    >
                      The minimum pre-approved recurring payment is {formatNumberToUSD(minRecurringPreapproved)}. Can we
                      bridge that gap? (Prompt PC or weekly payment)
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 3,
                  fontWeight: 'bold',
                  color: isHighRisk ? 'error.main' : 'inherit',
                }}
              >
                {isHighRisk ? 'High Risk Program' : 'Best Preauthorized Program'}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* Program results table */}
      {(showDownPayment || showRecurringAmount) && calculatedTerm > 0 && (
        <Grid container className="mt-4">
          <Grid container direction="row" className="table-header">
            <Grid item xs={4} className="mt-2 pl-2">
              <Typography variant="subtitle2" textAlign="left">
                Down Payment
              </Typography>
            </Grid>
            <Grid item xs={4} className="mt-2">
              <Typography variant="subtitle2" textAlign="center">
                Term
              </Typography>
            </Grid>
            <Grid item xs={4} className="mt-2 pr-2">
              <Typography variant="subtitle2" textAlign="right">
                Recurring Amount
              </Typography>
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={4} className="mt-2 pl-2">
              <Typography variant="subtitle2" textAlign="left">
                {formatNumberToUSD(customDownPmt)}
              </Typography>
            </Grid>
            <Grid item xs={4} className="mt-2 pl-2">
              <Typography variant="subtitle2" textAlign="center">
                {calculatedTermDisplay} {termLabel}
              </Typography>
            </Grid>
            <Grid item xs={4} className="mt-2 pl-2">
              <Typography variant="subtitle2" textAlign="right">
                {formatNumberToUSD(customRecurringAmtFinal)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}

      {isMobile && calculatedTerm > 0 && (
        <Grid container direction="row" justifyContent="center">
          <Typography variant="subtitle2" className={classes.fullWidth} textAlign="center">
            The first recurring payment will occur on {moment().add(1, isWeekly ? 'weeks' : 'months').format('MM/DD/YYYY')} and {eachPeriod}
            thereafter until the balance is paid in full
          </Typography>
        </Grid>
      )}

      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={2} className="mt-2">
          <MUIButton
            onClick={handleBack}
            variant="outlined"
            fullWidth
            sx={{
              backgroundColor: 'white',
              borderColor: '#e0e0e0',
              color: '#000',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd',
              },
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
          </MUIButton>
        </Grid>
        {isHighRisk && calculatedTerm > 0 ? (
          <Grid item xs={12} md={10} className="mt-2">
            <Button
              test-id="approve-high-risk-button"
              fullWidth
              onClick={handleProcessHre}
            >
              Process HRE
            </Button>
          </Grid>
        ) : (
          <Grid item xs={12} md={10} className="mt-2">
            <Button onClick={handleSelectCustomProgram} fullWidth test-id="payment-program-custom-add-payment">
              Add Payment Details
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
